import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';

import * as V from 'victory';

import './PerformanceChart.scss';
import { PerformanceFlyout } from '../';
import intervalForSlice from '../../../utilities/intervalForSlice';
import { ANALYTICS_COLORS } from '../../constants';

class PerformanceChart extends Component {
  constructor(props) {
    super(props);

    this.updateDimensions = this.updateDimensions.bind(this);
    this.state = {
      chartWidth: 0,
      hoverInterval: undefined
    };
  }

  componentDidMount() {
    this.setState({
      chartWidth: window.innerWidth
    });
    window.addEventListener('resize', this.updateDimensions);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimensions);
  }

  updateDimensions(event) {
    this.setState({
      chartWidth: event.target.innerWidth
    });
  }

  dataByAssetKey() {
    const data = {};

    this.props.details.forEach(slice => {
      const interval = intervalForSlice(this.props.intl)(
        slice,
        this.props.filter
      );
      const { selections_total, clicks_total } = slice;

      (this.props.performers || []).forEach((performer, idx) => {
        const { key } = performer;
        if (!data[key]) {
          data[key] = [];
        }

        const performerSlice = slice.data.find(row => row.key === key);
        if (performerSlice) {
          data[key].push({
            ...performerSlice,
            interval,
            selections_total,
            clicks_total
          });
        } else {
          data[key].push({
            key,
            interval,
            selections: 0,
            clicks: 0,
            ctor: 0.0
          });
        }
      });
    });
    return data;
  }

  dataByInterval() {
    const data = {};
    this.props.details.forEach(slice => {
      const interval = intervalForSlice(this.props.intl)(
        slice,
        this.props.filter
      );

      data[interval] = { ...slice, performers: [] };

      (this.props.performers || []).forEach(performer => {
        const performerSlice = slice.data.find(
          row => row.key === performer.key
        ) || { key: performer.key, selections: 0, clicks: 0, ctor: 0 };
        data[interval].performers.push({
          ...performer,
          slice: performerSlice
        });
      });
    });
    return data;
  }

  renderBar(entry, idx, isLast) {
    const row = entry[1];
    let style;
    if (entry[0]) {
      style = {
        data: {
          fill: ANALYTICS_COLORS[idx]
        }
      };
    }
    const tickFormat = tick => Math.round(tick);
    const name = isLast ? 'bar-last' : `bar-${idx}`;

    const labelFunc = () => '';
    const labelTooltip = (
      <V.VictoryTooltip
        flyoutComponent={
          <PerformanceFlyout
            data={this.dataByInterval()}
            chartType={this.props.chartToggle}
            performers={this.props.performers}
          />
        }
      />
    );

    const barWidth = this.props.details.length < 6 ? 150 : null;
    const labels = isLast ? labelFunc : undefined;
    const label = isLast ? labelTooltip : undefined;
    return (
      <V.VictoryBar
        name={name}
        key={`bar-${idx}`}
        data={row.map(r => ({ ...r, isLast }))}
        style={style}
        barRatio={0.8}
        barWidth={barWidth}
        x="interval"
        y="selections"
        labelComponent={label}
        labels={labels}
        tickFormat={tickFormat}
      />
    );
  }
  renderHoverLine() {
    const chartType = this.props.chartToggle;
    const tickFormat = tick => Math.round(tick);
    if (this.state.hoverInterval) {
      const data = this.dataByInterval()[this.state.hoverInterval];
      if (data) {
        const maxPerfData = Math.max(
          ...data.performers.map(perf => perf.slice[chartType] || 0)
        );

        const style = {
          data: {
            fill: 'rgb(176, 173, 171)'
          }
        };

        const scatterData = data.performers.map(perf => ({
          ...perf.slice,
          interval: this.state.hoverInterval
        }));
        const scatterStyle = {
          data: {
            fill: ({ datum }) =>
              ANALYTICS_COLORS[
                data.performers.findIndex(perf => perf.key === datum.key)
              ],
            stroke: 'rgb(221, 219, 218)'
          }
        };
        return (
          <V.VictoryGroup>
            <V.VictoryBar
              key="line-hover"
              data={[
                { interval: this.state.hoverInterval, [chartType]: maxPerfData }
              ]}
              barWidth={1}
              x="interval"
              y={chartType}
              style={style}
              tickFormat={tickFormat}
            />
            <V.VictoryScatter
              data={scatterData}
              style={scatterStyle}
              x="interval"
              y={chartType}
              tickFormat={tickFormat}
            />
          </V.VictoryGroup>
        );
      }
    }
  }

  renderLine(entry, idx) {
    const row = entry[1];
    let style;
    if (entry[0]) {
      style = {
        data: {
          stroke: ANALYTICS_COLORS[idx]
        }
      };
    }

    return (
      <V.VictoryLine
        key={`line-${idx}`}
        data={row}
        style={style}
        x="interval"
        y={this.props.chartToggle}
      />
    );
  }

  renderLegend(entry, idx) {
    if (entry[0]) {
      return { name: entry[0], symbol: { type: 'square' } };
    }
  }

  legendItems() {
    let itemsPerRow = 5;
    let startOfRow;
    let endOfRow;
    this.props.performers.forEach(perf => {
      if (perf.key?.length > 104) {
        itemsPerRow = 1;
      }
    });
    if (itemsPerRow > 1) {
      for (let row = 0; row < 10; row++) {
        let line = 0;
        const maxRowLength = 140;
        startOfRow = itemsPerRow * row;
        endOfRow = startOfRow + (itemsPerRow - 1);
        this.props.performers
          .slice(startOfRow, endOfRow)
          .map(perf => (line += perf.key?.length));
        if (line > maxRowLength) {
          itemsPerRow = itemsPerRow - Math.ceil(line / maxRowLength);
        }
      }
    }
    return itemsPerRow;
  }

  render() {
    const { formatMessage, formatNumber } = this.props.intl;
    const legendItemsPerRow = this.legendItems();
    const rowLength = Math.ceil(
      this.props.performers.length / legendItemsPerRow
    );
    const chartType = this.props.chartToggle;
    const events = chartType => {
      return [
        {
          childName: 'all',
          target: 'data',
          eventHandlers: {
            onMouseOver: (evt, target) => {
              this.setState({ hoverInterval: target?.datum?.interval });
              return [
                {
                  childName: 'bar-last',
                  target: 'labels',
                  mutation: () => ({ active: true })
                }
              ];
            },
            onMouseOut: () => {
              this.setState({ hoverInterval: undefined });
              return [
                {
                  childName: 'bar-last',
                  target: 'labels',
                  mutation: () => ({ active: undefined })
                }
              ];
            }
          }
        }
      ];
    };

    const data = Object.entries(this.dataByAssetKey());
    let xDomainPadding;
    if (this.props.details.length < 5) {
      xDomainPadding = 1500 / this.props.details.length;
    } else if (this.props.details.length === 24) {
      xDomainPadding = 30;
    } else {
      xDomainPadding = 70;
    }
    const tickFormat =
      chartType === 'ctor'
        ? tick =>
            formatNumber(tick, { style: 'percent', maximumFractionDigits: 2 })
        : tick => Math.round(tick);
    const domain = chartType === 'clicks' ? [0, 5] : null;
    const lineChartLabel =
      chartType === 'ctor'
        ? formatMessage({ id: 'components.ecs.analytics.perf.ctorChart.title' })
        : formatMessage({
            id: 'components.ecs.analytics.perf.clickChart.title'
          });

    return (
      <div className="performance-chart">
        <V.VictoryChart
          padding={{ left: 90, bottom: 25 }}
          domainPadding={{ x: xDomainPadding, y: 80 }}
          width={this.state.chartWidth}
        >
          <V.VictoryAxis
            theme={V.VictoryTheme.material}
            style={{
              axis: { stroke: 'rgb(235, 234, 233)' },
              tickLabels: {
                fontFamily: "'Salesforce Sans', sans-serif",
                fontSize: 12,
                fill: 'rgb(107, 109, 112)'
              }
            }}
            fixLabelOverlap={true}
          />
          <V.VictoryAxis
            dependentAxis
            tickFormat={tickFormat}
            domain={domain}
            theme={V.VictoryTheme.material}
            label={lineChartLabel}
            axisLabelComponent={
              <V.VictoryLabel dy={-40} style={{ fill: 'rgb(107, 109, 112)' }} />
            }
            style={{
              axis: { stroke: 'transparent' },
              tickLabels: {
                fontFamily: "'Salesforce Sans', sans-serif",
                fontSize: 12,
                fill: 'rgb(107, 109, 112)'
              },
              grid: {
                stroke: 'rgb(235, 234, 233)'
              }
            }}
          />
          {data.map((entry, idx) => this.renderLine(entry, idx))}
          {this.renderHoverLine()}
        </V.VictoryChart>
        <V.VictoryChart
          padding={{ left: 90, bottom: 25 }}
          domainPadding={{ x: xDomainPadding, y: 80 }}
          width={this.state.chartWidth}
          height={this.props.chartHeight}
        >
          <V.VictoryAxis
            theme={V.VictoryTheme.material}
            style={{
              axis: { stroke: 'rgb(235, 234, 233)' },
              tickLabels: {
                fontFamily: "'Salesforce Sans', sans-serif",
                fontSize: 12,
                fill: 'rgb(107, 109, 112)'
              }
            }}
            fixLabelOverlap={true}
          />
          <V.VictoryAxis
            dependentAxis
            tickFormat={tick => Math.round(tick)}
            domain={[0, 5]}
            theme={V.VictoryTheme.material}
            label={formatMessage({
              id: 'components.ecs.analytics.perf.selectionChart.title'
            })}
            axisLabelComponent={
              <V.VictoryLabel dy={-40} style={{ fill: 'rgb(107, 109, 112)' }} />
            }
            style={{
              axis: { stroke: 'transparent' },
              tickLabels: {
                fontFamily: "'Salesforce Sans', sans-serif",
                fontSize: 12,
                fill: 'rgb(107, 109, 112)'
              },
              grid: {
                stroke: 'rgb(235, 234, 233)'
              }
            }}
          />
          <V.VictoryStack events={events('bar')}>
            {data.map((entry, idx) =>
              this.renderBar(entry, idx, idx === data.length - 1)
            )}
          </V.VictoryStack>
        </V.VictoryChart>
        <V.VictoryLegend
          x={10}
          y={10}
          height={(rowLength + 1) * 8}
          theme={V.VictoryTheme.material}
          gutter={10}
          itemsPerRow={legendItemsPerRow}
          symbolSpacer={3}
          orientation="horizontal"
          style={{
            labels: {
              fontFamily: "'Salesforce Sans', sans-serif",
              fontSize: 4,
              fill: 'rgb(107, 109, 112)'
            }
          }}
          colorScale={ANALYTICS_COLORS}
          data={data.map((entry, idx) => this.renderLegend(entry, idx))}
        />
      </div>
    );
  }
}

PerformanceChart.propTypes = {
  actions: PropTypes.object,
  chartToggle: PropTypes.string,
  filter: PropTypes.object,
  details: PropTypes.array,
  performers: PropTypes.array,
  location: PropTypes.object
};

export default injectIntl(PerformanceChart);
